import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  authStore,
  completeSsoLogin,
  sanitizeReturnTo,
  useSsoAuth,
  type SsoLoginFailureReason,
  type SsoLoginOutcome,
} from '@wehunt/core-web'
import { AuthShell, AuthHeader, COLORS } from '@wehunt/ui'

/**
 * A record rather than an interpolated key, so renaming a failure reason is a
 * compile error here instead of a missing translation on the page nobody
 * looks at until a login is already broken.
 */
const FAILURE_KEY: Record<SsoLoginFailureReason, string> = {
  invalid_state: 'sso.failed.invalid_state',
  exchange_failed: 'sso.failed.exchange_failed',
  sign_in_failed: 'sso.failed.sign_in_failed',
}

/** What this page renders when the attempt did not complete. */
type FailedOutcome = Extract<SsoLoginOutcome, { kind: 'failed' }>

/**
 * Cap on waiting for the auth store to settle before judging a spent state.
 *
 * The store settles as soon as Firebase has read its persisted session, which
 * is fast, but "signed in" is the only answer that changes what this page
 * does -- so a store that never settles has to read as not signed in rather
 * than hold a spinner over an error the user could act on.
 */
const AUTH_SETTLE_TIMEOUT_MS = 5000

/**
 * Whether a session exists, once the auth store has actually decided.
 *
 * A snapshot is worthless at this point. Every arrival at /auth/callback is a
 * fresh document load, the root guard returns early for this route WITHOUT
 * awaiting `waitUntilSettled()` (it must, since the callback is what creates
 * the session), and the store is seeded only when the provider's
 * `onAuthStateChanged` fires after an asynchronous persistence read. The
 * invalid_state outcome lands one microtask after mount, long before that, so
 * a snapshot reads "loading" and a signed-in user would be shown the
 * dead-link page anyway.
 */
async function isSignedInOnceSettled(): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const capped = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), AUTH_SETTLE_TIMEOUT_MS)
  })
  try {
    const state = await Promise.race([authStore.waitUntilSettled(), capped])
    return state?.status === 'signed-in'
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Leaves the callback by a full document navigation, so a fresh page load
 * with the auth store already settled is what the app resumes into.
 */
function leavePageByFullLoad(href: string): void {
  window.location.replace(href)
}

/** The landing page for sso's redirect back to us, at /auth/callback. */
export function SsoCallbackPage({
  leavePage = leavePageByFullLoad,
}: {
  /**
   * Overridable so tests can observe the navigation: jsdom does not let a
   * spy replace window.location.replace.
   */
  leavePage?: (href: string) => void
} = {}) {
  const { startSsoLogin } = useSsoAuth()
  const { t } = useTranslation('app')
  const [failure, setFailure] = useState<FailedOutcome | null>(null)

  // The authorization code and the stored state are both single-use, so this
  // must run exactly once. React's StrictMode invokes mount effects twice in
  // development, and a second run would report invalid_state for a login that
  // in fact succeeded. The ref survives that simulated remount.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    void completeSsoLogin(window.location).then(
      async (outcome) => {
        if (outcome.kind === 'signed-in') {
          // A router navigation out of this page re-enters the root route's
          // async beforeLoad, which puts the root match into a pending state
          // during the transition. In the router version this app is on that
          // pending root match threw `undefined` from its renderer on every
          // first sign-in in the rehearsal, crashing to the top-level error
          // boundary with no page component in the stack. A full page load
          // sidesteps that transition entirely: it costs one reload of a page
          // the user just loaded anyway, and `replace` leaves Back unable to
          // return to the spent callback. Resolving against this origin is NOT
          // a guard on its own: the URL parser strips tabs and newlines first,
          // so "/<tab>/evil.example" resolves off-site. Sanitising here as well
          // as in completeSsoLogin keeps this location assignment safe
          // whatever the outcome it is handed.
          leavePage(new URL(sanitizeReturnTo(outcome.returnTo), window.location.origin).href)
          return
        }
        // Back, from anywhere in the app after a login, arrives here: the
        // previous history entry is sso's own page, which re-runs its
        // authorize request and redirects to this callback with a state this
        // tab has already spent. Reporting a dead link to someone who IS
        // signed in reports the failure of something they never asked for, so
        // send them into the app instead.
        if (outcome.reason === 'invalid_state' && (await isSignedInOnceSettled())) {
          leavePage(new URL('/', window.location.origin).href)
          return
        }
        setFailure(outcome)
      },
      (err: unknown) => {
        console.error('[SsoCallbackPage] completing the sso login threw', err)
        setFailure({ kind: 'failed', reason: 'exchange_failed' })
      },
    )
  }, [])

  if (!failure) {
    return (
      <AuthShell>
        <AuthHeader
          logoSrc="/assets/icon-192.png"
          logoAlt="WeHunt"
          align="center"
          title={t('sso.redirecting')}
          subtitle={t('signIn.subtitle')}
        />
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <AuthHeader
        logoSrc="/assets/icon-192.png"
        logoAlt="WeHunt"
        align="center"
        title={t('signIn.title')}
        subtitle={t('signIn.subtitle')}
      />
      <p role="alert" style={{ margin: '0 0 16px', color: COLORS.error, fontSize: 14 }}>
        {t(FAILURE_KEY[failure.reason])}
      </p>
      <button
        type="button"
        className="wh-btn wh-btn-primary"
        onClick={() => {
          // Retry into where the login was headed, when the failure knew it:
          // a user who was opening a hunt should land on that hunt.
          startSsoLogin(failure.returnTo).catch((err: unknown) => {
            console.error('[SsoCallbackPage] retrying the sso login failed', err)
          })
        }}
        style={{ height: 52, width: '100%', borderRadius: 12, fontSize: 16, fontWeight: 700 }}
      >
        {t('sso.retry')}
      </button>
    </AuthShell>
  )
}
